export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export const TYPESENSE_GEOLOC_FIELD = 'geoloc';

/**
 * Typesense's polygon geo filter silently drops matching documents, sometimes to zero, once the
 * queried polygon's longitude span gets wide, even though a wider box can only ever be a strict
 * superset of a narrower one, confirmed directly against the live index (a box that fully
 * contained ~7,000 known Finland jobs the whole time went 6,959 -> 3,179 -> 100 -> 0 as only its
 * longitude range widened past roughly 40-130 degrees, depending on the box's exact shape). This
 * matches a long-standing, seemingly still-unresolved upstream report:
 * https://github.com/typesense/typesense/issues/648. It cost nothing before TEC-482 unlocked
 * pan/zoom past Finland's own ~13-degree-wide longitude range, but any "zoom out to see other
 * countries" view can trivially exceed it.
 *
 * The single most reliable mitigation found by testing: splitting a wide box into several
 * narrower boxes OR'd together, each within this safe span, rather than one wide polygon.
 * Verified against the live index: a single box spanning most of the globe returns 0 hits, while
 * the same area split into 90-degree-wide tiles and OR'd together returns the correct total
 * (matching the sum of each tile queried alone, and matching the count of every document that
 * has a geoloc at all). 90 was chosen because every tile at or under that width tested clean in
 * isolation; this is an empirically-safe value from real testing, not a documented Typesense
 * guarantee, so treat it as a mitigation for the known-bad shapes this map actually produces,
 * not a proof the bug can never resurface at a narrower span for some other shape.
 */
const SAFE_GEO_FILTER_LONGITUDE_SPAN_DEG = 90;

/** One tile's worth of the SW -> SE -> NE -> NW polygon corner order Typesense's geosearch expects. */
function buildSingleBoxGeoFilterClause(bounds: MapBounds, field: string): string {
  const { north, south, east, west } = bounds;
  const pairs = [south, west, south, east, north, east, north, west];
  return `${field}:(${pairs.join(', ')})`;
}

/**
 * Typesense polygon filter for a rectangular map viewport. Tiles the longitude range into
 * several OR'd sub-boxes once it's wide enough to hit the Typesense bug described above; see
 * SAFE_GEO_FILTER_LONGITUDE_SPAN_DEG's doc comment. A single tile (the common case: Finland's
 * own ~13-degree-wide viewport, or any other reasonably tight zoom) is returned unwrapped, same
 * as before this fix.
 */
export function buildTypesenseBoundingBoxGeoFilter(
  bounds: MapBounds,
  field = TYPESENSE_GEOLOC_FIELD,
): string {
  const { north, south, east, west } = bounds;
  const span = east - west;
  // A non-positive span means the caller's box is degenerate or crosses the antimeridian in a
  // shape this function has never specially handled (west > east); fall back to the original,
  // untiled single-polygon behavior rather than guessing at a wraparound split.
  if (!(span > SAFE_GEO_FILTER_LONGITUDE_SPAN_DEG)) {
    return buildSingleBoxGeoFilterClause({ north, south, east, west }, field);
  }

  const tileCount = Math.ceil(span / SAFE_GEO_FILTER_LONGITUDE_SPAN_DEG);
  const tileWidth = span / tileCount;
  const tiles: string[] = [];
  for (let i = 0; i < tileCount; i++) {
    const tileWest = west + i * tileWidth;
    // Last tile's east edge is the real `east`, not a rounded multiple of tileWidth, so the
    // tiles exactly cover the original span with no gap or overlap from floating-point drift.
    const tileEast = i === tileCount - 1 ? east : tileWest + tileWidth;
    tiles.push(buildSingleBoxGeoFilterClause({ north, south, east: tileEast, west: tileWest }, field));
  }
  return `(${tiles.join(' || ')})`;
}

/**
 * Algolia `insideBoundingBox`: [[south, west, north, east]].
 */
export function buildTypesenseGeoFilterFromAlgoliaInsideBoundingBox(
  insideBoundingBox: number[][],
  field = TYPESENSE_GEOLOC_FIELD,
): string | undefined {
  const box = insideBoundingBox[0];
  if (!box || box.length < 4) return undefined;
  const [south, west, north, east] = box;
  if (
    ![south, west, north, east].every((n) => typeof n === 'number' && Number.isFinite(n))
  ) {
    return undefined;
  }
  return buildTypesenseBoundingBoxGeoFilter({ south, west, north, east }, field);
}
